import React, { Fragment, useContext, useEffect, useState } from "react";
import { Button, Card, Col, Container, Form, OverlayTrigger, Row, Table, Tooltip } from "react-bootstrap";
import { abpnClient } from "../../Services/AbpnClient";
import { AuthContext } from "../Auth/AuthProvider";
import CCActivity from "../Physician/Models/CCActivity";
import Physician from "../Physician/Models/Physician";
import AbpnSpinner from "../Shared/Components/AbpnSpinner";
import { deepCopy } from "../Shared/Utility/DeepCopy";
import SelfReportedActivityRow from "./SelfReportedActivityRow";
import InputMask from 'react-input-mask';
import { getDateWithOffset } from "../Shared/Utility/GetDateWithOffset";
import { Redirect } from "react-router";

export default function ManageActivities() {
    const [physician, setPhysician] = useState<Physician>(null);
    const [verifiedActivities, setVerifiedActivities] = useState<Array<CCActivity>>(null);
    const [verifiedActivitiesStartDate, setVerifiedActivitiesStartDate] = useState<string>(null);
    const [verifiedActivitiesEndDate, setVerifiedActivitiesEndDate] = useState<string>(null);
    const [selfReportedActivities, setSelfReportedActivities] = useState<Array<CCActivity>>(null);
    const [selfReportedActivitiesStartDate, setSelfReportedActivitiesStartDate] = useState<string>(null);
    const [selfReportedActivitiesEndDate, setSelfReportedActivitiesEndDate] = useState<string>(null);
    const [filteredSelfReportedActivities, setFilteredSelfReportedActivities] = useState<Array<CCActivity>>(null);
    const [filterInputUpdate, setFilterInputUpdate] = useState(0);

    const authContext = useContext(AuthContext);

    useEffect(() => {
        abpnClient.get<Physician>("/api/physician", authContext)
            .then(_ => {
                setPhysician(_);
                let dateSort = (a: CCActivity, b: CCActivity) => {
                    return getDateWithOffset(b.completedDate).getTime() - getDateWithOffset(a.completedDate).getTime();
                }
                let sortedVerifiedActivities = _.ccVerifiedActivities.sort(dateSort);
                setVerifiedActivities(sortedVerifiedActivities);
                let sortedSelfReportedActivities = _.ccSelfReportedActivities.sort(dateSort);
                setSelfReportedActivities(sortedSelfReportedActivities);
            });
    }, []);

    useEffect(() => {
        if (physician?.ccVerifiedActivities) {
            let filterededVerifiedActivities = physician.ccVerifiedActivities;
            let startDate = getDateWithOffset(verifiedActivitiesStartDate)?.getTime();
            if (startDate) {
                filterededVerifiedActivities = filterededVerifiedActivities.filter(_ => 
                    getDateWithOffset(_.completedDate).getTime() >= getDateWithOffset(verifiedActivitiesStartDate).getTime()
                );
            }
            let endDate = getDateWithOffset(verifiedActivitiesEndDate)?.getTime();
            if (endDate) {
                filterededVerifiedActivities = filterededVerifiedActivities.filter(_ => 
                    getDateWithOffset(_.completedDate).getTime() <= endDate
                );
            }
            setVerifiedActivities(filterededVerifiedActivities);
        }
    }, [verifiedActivitiesStartDate, verifiedActivitiesEndDate]);

    useEffect(() => {
        if (selfReportedActivities) {
            let newFilterededSelfReportedActivities = deepCopy(selfReportedActivities);
            let startDate = getDateWithOffset(selfReportedActivitiesStartDate)?.getTime();
            if (startDate) {
                newFilterededSelfReportedActivities = newFilterededSelfReportedActivities.filter(_ => 
                    getDateWithOffset(_.completedDate).getTime() >= startDate
                );
            }
            let endDate = getDateWithOffset(selfReportedActivitiesEndDate)?.getTime();
            if (endDate) {
                newFilterededSelfReportedActivities = newFilterededSelfReportedActivities.filter(_ => 
                    getDateWithOffset(_.completedDate).getTime() <= endDate
                );
            }
            setFilteredSelfReportedActivities(newFilterededSelfReportedActivities);
        }
    }, [selfReportedActivitiesStartDate, selfReportedActivitiesEndDate, selfReportedActivities]);

    const addNewSelfReportedActivity = () => {
        clearSelfReportedFitler();
        var newSelfReportedActivity = new CCActivity();
        newSelfReportedActivity.isNew = true;
        newSelfReportedActivity.isEditing = true;
        var newList = deepCopy(selfReportedActivities);
        newList.unshift(newSelfReportedActivity);
        setSelfReportedActivities(newList);
        
        let table = document.getElementById("selfReportedActivitiesTable");
        if (table) table.scrollTop = 0;
    }

    const removeSelfReportedActivity = (selfReportedActivity: CCActivity) => {
        if (!selfReportedActivity.isNew) {
            const url = "/api/Physician/DeleteCCSelfReportedActivity/";
            abpnClient.post<CCActivity>(url, selfReportedActivity, authContext).then(() => {
                var newList = deepCopy(selfReportedActivities.filter(_ => _.ccActivityId !== selfReportedActivity.ccActivityId));
                setSelfReportedActivities(newList);
            });
        }
        else {
            var newList = deepCopy(selfReportedActivities.filter(_ => _.ccActivityId !== selfReportedActivity.ccActivityId));
            setSelfReportedActivities(newList);
        }
    }

    const updateSelfReportedActivity = (ccActivityId: number, selfReportedActivity: CCActivity) => {
        var updated = deepCopy(selfReportedActivities);
        var index = updated.findIndex(_ => _.ccActivityId == ccActivityId);
        updated[index] = selfReportedActivity;
        setSelfReportedActivities(updated);
    }

    const canAddNew = () => {
        var hasNewSelfReportedActivity = selfReportedActivities?.find(_ => _.isNew);
        return !hasNewSelfReportedActivity;
    }

    const clearVerifiedFilter = () => {
        setVerifiedActivitiesStartDate(null);
        setVerifiedActivitiesEndDate(null);
        forceFilterInputUpdate();
    }

    const clearSelfReportedFitler = () => {
        setSelfReportedActivitiesStartDate(null);
        setSelfReportedActivitiesEndDate(null);
        forceFilterInputUpdate();
    }

    const forceFilterInputUpdate = () => {
        //workaround for InputMask not rerendering correctly
        setFilterInputUpdate(filterInputUpdate + 1);
    }

    if (!physician) return <AbpnSpinner></AbpnSpinner>

    if (physician.isDansInvalidatedAllowReentryExam) return <Redirect to="/" ></Redirect>;
    
    return (
        <Container>
            <Row>
                <Col>
                    <Card className="mb-3" border="pp-secondary-grey">
                        <Card.Body>
                            <Card.Title className="font-weight-bolder">
                                VERIFIED ACTIVITIES
                                <OverlayTrigger
                                    placement={"right"}
                                    overlay={<Tooltip id="verifiedActivities" bsPrefix="pp-tooltip">
                                        Records of completed CC credits and activities from various professional organizations are reported directly to ABPN within three months of the activity completion. In the event of an audit, no further documentation is required from a diplomate for verified activities.
                                    </Tooltip>}>
                                    <i className="las la-info-circle ml-1 text-pp-secondary"></i>
                                </OverlayTrigger>
                            </Card.Title>
                            <div className="pp-font-size-smaller mb-3">
                                Records of completed CC credits from select professional organizations are reported directly to ABPN within approximately three months of the activity completion. The verified credits are reflected in the CC Activity Attestations section.  In the event of an audit no further documentation is required from a diplomate for verified activities.
                            </div>
                            <Row className="align-items-end">
                                <Col xs={12} md={2}>
                                    <Form.Label className="font-weight-bolder mb-1 pp-font-size-smaller">Display From</Form.Label>
                                    <InputMask mask="99/99/9999"
                                        key={JSON.stringify(filterInputUpdate)}
                                        onBlur={(e: any) => setVerifiedActivitiesStartDate(e.target.value)}>
                                        {(inputProps: any) => {
                                            return <Form.Control {...inputProps} type="input"/>
                                        }}
                                    </InputMask>
                                </Col>
                                <Col xs={12} md={2}>
                                    <Form.Label className="font-weight-bolder mb-1 pp-font-size-smaller">To</Form.Label>
                                    <InputMask mask="99/99/9999"
                                        key={JSON.stringify(filterInputUpdate)}
                                        onBlur={(e: any) => setVerifiedActivitiesEndDate(e.target.value)}>
                                        {(inputProps: any) => {
                                            return <Form.Control {...inputProps} type="input" />
                                        }}
                                    </InputMask>
                                </Col>
                                <Col>
                                    <Button 
                                        variant="outline-dark"
                                        onClick={clearVerifiedFilter}>
                                        Clear
                                    </Button>
                                </Col>
                            </Row>
                            {(!verifiedActivities || verifiedActivities.length === 0) ?
                                <div className="pp-font-size-smaller mt-2">
                                    No Verified Activities on file. It may take up to three months from the event date to be reported.
                                </div>
                                :
                                <Fragment>
                                    <div className="pp-activities-table">
                                        <Table className="pp-activities-table pp-font-size-smaller">
                                            <thead>
                                                <tr>
                                                    <th>
                                                        <div className="mb-1 pp-table-header">Activity Name</div>
                                                    </th>
                                                    <th>
                                                        <div className="mb-1 pp-table-header">Organization</div>
                                                    </th>
                                                    <th>
                                                        <div className="mb-1 pp-table-header">Completed</div>
                                                    </th>
                                                    <th>
                                                        <div className="mb-1 pp-table-header"><a href="https://abpn.org/maintain-certification/moc-activity-requirements/continuing-medical-education-cme/">CME</a></div>
                                                    </th>
                                                    <th>
                                                        <div className="mb-1 pp-table-header"><a href="https://abpn.org/maintain-certification/moc-activity-requirements/self-assessment-sa/">SA CME</a></div>
                                                    </th>
                                                    <th>
                                                        <div className="mb-1 pp-table-header"><a href="https://abpn.org/maintain-certification/moc-activity-requirements/improvement-in-medical-practice-pip/clinical-chart-review-module/">PIP Clinical</a></div>
                                                    </th>
                                                    <th>
                                                        <div className="mb-1 pp-table-header"><a href="https://abpn.org/maintain-certification/moc-activity-requirements/improvement-in-medical-practice-pip/patient-or-peer-feedback-module/">PIP Feedback</a></div>
                                                    </th>
                                                    <th>
                                                        <div className="mb-1 pp-table-header"><a href="https://abpn.org/maintain-certification/moc-activity-requirements/patient-safety-activity/">Patient Safety</a></div>
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {verifiedActivities.map(_ =>
                                                    <tr>
                                                        <td>
                                                            {_.activityName}
                                                        </td>
                                                        <td>
                                                            {_.organizationName}
                                                        </td>
                                                        <td>
                                                            {getDateWithOffset(_.completedDate).toLocaleDateString()}
                                                        </td>
                                                        <td>
                                                            {_.cmeCredits}
                                                        </td>
                                                        <td>
                                                            {_.saCredits}
                                                        </td>
                                                        <td>
                                                            {_.isPipClinical ? <i className="font-weight-bolder las la-check pp-icon-secondary"></i> : "-"}
                                                        </td>
                                                        <td>
                                                            {_.isPipFeedback ? <i className="font-weight-bolder las la-check pp-icon-secondary"></i> : "-"}
                                                        </td>
                                                        <td>
                                                            {_.isPatientSafety ? <i className="font-weight-bolder las la-check pp-icon-secondary"></i> : "-"}
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </Table>
                                    </div>
                                    <hr />
                                    <div className="pp-font-size-smaller mt-2">Allow up to three months from the activity completion date for the credits to be reported to ABPN.</div>
                                </Fragment>
                            }
                        </Card.Body>
                    </Card>
                    <Card className="mb-3" border="pp-secondary-grey">
                        <Card.Body>
                            <Card.Title className="font-weight-bolder">
                                SELF-REPORTED ACTIVITIES - Optional Log for CME Certification Activities
                            </Card.Title>
                            <div className="pp-font-size-smaller mb-3">
                                This log is not reviewed by ABPN and does not count towards CC Program participation. It is an optional tool for tracking certification activities that are not third-party verified. Entries in this log will not be added to the Activities Section of your Physician Portal.<br /><br />
                                You cannot upload and save CME documentation to the ABPN Physician Portal, so please keep copies in the event of an audit.<br /><br />
                                To receive CC Program credit, enter your completed activities in the "Activity Requirements" section of the Physician Portal.<br /><br />
                                <b>Click on the CC activity below to view detailed descriptions of how to meet the requirements.</b>
                            </div>
                            <Button variant="pp-primary" className="mb-1" onClick={() => addNewSelfReportedActivity()} disabled={!canAddNew()}>Add</Button>
                                <Row className="align-items-end">
                                    <Col xs={12} md={2}>
                                        <Form.Label className="font-weight-bolder mb-1 pp-font-size-smaller">Display From</Form.Label>
                                        <InputMask mask="99/99/9999"
                                            key={JSON.stringify(filterInputUpdate)}
                                            onBlur={(e: any) => setSelfReportedActivitiesStartDate(e.target.value)}>
                                            {(inputProps: any) => {
                                                return <Form.Control {...inputProps} type="input" />
                                            }}
                                        </InputMask>
                                    </Col>
                                    <Col xs={12} md={2}>
                                        <Form.Label className="font-weight-bolder mb-1 pp-font-size-smaller">To</Form.Label>
                                        <InputMask mask="99/99/9999"
                                            key={JSON.stringify(filterInputUpdate)}
                                            onBlur={(e: any) => setSelfReportedActivitiesEndDate(e.target.value)}>
                                            {(inputProps: any) => {
                                                return <Form.Control {...inputProps} type="input" />
                                            }}
                                        </InputMask>
                                    </Col>
                                    <Col>
                                        <Button 
                                            variant="outline-dark"
                                            onClick={clearSelfReportedFitler}>
                                            Clear
                                        </Button>
                                    </Col>
                                </Row>
                            {(!filteredSelfReportedActivities || filteredSelfReportedActivities.length === 0) ?
                                <div className="pp-font-size-smaller mt-2">
                                    No Self-reported Activities on file. Click the Add button to enter your Self-reported activity. This is to help you keep track of your records.
                                </div>
                                :
                                <Fragment>
                                    <div className="pp-activities-table" id="selfReportedActivitiesTable">
                                        <Table className="pp-activities-table pp-font-size-smaller">
                                            <thead>
                                                <tr>
                                                    <th>
                                                        <div className="mb-1 pp-table-header">Activity Name</div>
                                                    </th>
                                                    <th>
                                                        <div className="mb-1 pp-table-header">Organization</div>
                                                    </th>
                                                    <th>
                                                        <div className="mb-1 pp-table-header">Completed</div>
                                                    </th>
                                                    <th>
                                                        <div className="mb-1 pp-table-header"><a href="https://abpn.org/maintain-certification/moc-activity-requirements/continuing-medical-education-cme/">CME</a></div>
                                                    </th>
                                                    <th>
                                                        <div className="mb-1 pp-table-header"><a href="https://abpn.org/maintain-certification/moc-activity-requirements/self-assessment-sa/">SA CME</a></div>
                                                    </th>
                                                    <th>
                                                        <div className="mb-1 pp-table-header"><a href="https://abpn.org/maintain-certification/moc-activity-requirements/improvement-in-medical-practice-pip/clinical-chart-review-module/">PIP Clinical</a></div>
                                                    </th>
                                                    <th>
                                                        <div className="mb-1 pp-table-header"><a href="https://abpn.org/maintain-certification/moc-activity-requirements/improvement-in-medical-practice-pip/patient-or-peer-feedback-module/">PIP Feedback</a></div>
                                                    </th>
                                                    <th>
                                                        <div className="mb-1 pp-table-header"><a href="https://abpn.org/maintain-certification/moc-activity-requirements/patient-safety-activity/">Patient Safety</a></div>
                                                    </th>
                                                    <th>
                                                        <div className="mb-1 pp-table-header">Actions</div>
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredSelfReportedActivities.map(_ =>
                                                    <SelfReportedActivityRow 
                                                        selfReportedActivity={_} 
                                                        key={_.ccActivityId} 
                                                        handleRemove={removeSelfReportedActivity} 
                                                        handleUpdate={updateSelfReportedActivity} />
                                                )}
                                            </tbody>
                                        </Table>
                                    </div>
                                    <hr />
                                </Fragment>
                            }
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container >
    );
}